import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Card, 
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormControl,
  FormLabel,
  InputAdornment,
  Link
} from '@mui/material';
import { useConfig } from '../context/ConfigContext';
import { trackEvent, getSalaryRangeBin } from '../analytics';
import { 
  getAnnualTakeHomeFromGross,
  formatCurrencyGBP,
  getInputModeFromStorage,
  setInputModeToStorage,
  monthlyToAnnual,
  annualToMonthly
} from '../lib/takeHome';
import { 
  makeMeta,
  makeWebApplicationJsonLd,
  makeBreadcrumbJsonLd 
} from '../utils/seo';

const TakeHomeSimple = () => {
  const { config } = useConfig();
  
  // State for form inputs
  const [inputMode, setInputMode] = useState('annual');
  const [salaryInput, setSalaryInput] = useState('');
  const [error, setError] = useState('');
  
  // State for results
  const [results, setResults] = useState(null);

  // Initialize input mode from localStorage and URL params
  useEffect(() => {
    // Check URL params first
    const urlParams = new URLSearchParams(window.location.search);
    const urlMode = urlParams.get('mode');
    
    if (urlMode === 'annual' || urlMode === 'monthly') {
      setInputMode(urlMode);
      setInputModeToStorage(urlMode);
    } else {
      // Use stored preference
      setInputMode(getInputModeFromStorage());
    }
  }, []);

  // Handle input mode change
  const handleInputModeChange = (event) => {
    const newMode = event.target.value;
    setInputMode(newMode);
    setInputModeToStorage(newMode);
    
    // Clear results when switching modes
    setResults(null);
    setError('');
  };

  // Handle salary input change
  const handleSalaryChange = (event) => {
    const value = event.target.value;
    setSalaryInput(value);
    setError('');
    setResults(null);
  };

  // Validate salary input
  const validateSalary = (value) => {
    if (!value || value.trim() === '') {
      return 'Please enter a salary amount';
    }
    
    const numericValue = parseFloat(value.replace(/[£,\s]/g, ''));
    
    if (isNaN(numericValue)) {
      return 'Please enter a valid number';
    }
    
    if (numericValue <= 0) {
      return 'Salary must be greater than £0';
    }
    
    return null;
  };

  // Handle form submission
  const handleCalculate = () => {
    const validationError = validateSalary(salaryInput);
    if (validationError) {
      setError(validationError);
      return;
    }

    // Parse input value
    const inputValue = parseFloat(salaryInput.replace(/[£,\s]/g, ''));
    
    // Convert to annual if needed
    const annualGross = inputMode === 'monthly' ? monthlyToAnnual(inputValue) : inputValue;
    
    // Calculate take-home
    const annualTakeHome = getAnnualTakeHomeFromGross(annualGross, config);
    const monthlyTakeHome = annualToMonthly(annualTakeHome);
    
    setResults({
      annual: annualTakeHome,
      monthly: monthlyTakeHome,
      inputAnnual: annualGross,
      inputValue: inputValue
    });

    // Track analytics event
    trackEvent('simple_take_home_calculated', {
      input_mode: inputMode,
      salary_range: getSalaryRangeBin(annualGross),
      result_annual_range: getSalaryRangeBin(annualTakeHome),
      tax_year: config?.taxYear || '2025',
      nation: config?.nation || 'england_wales',
      page_type: 'tool'
    });
  };

  // Enhanced SEO metadata and structured data
  const canonicalUrl = 'https://salarytools.co.uk/tools/take-home-simple';
  const pageTitle = 'Simple Take-Home Calculator UK';
  const pageDescription = 'Quick and simple UK take-home pay calculator. Enter your salary and instantly see your monthly and annual take-home pay after tax, National Insurance and other deductions.';
  
  const webAppJsonLd = makeWebApplicationJsonLd({
    name: 'Simple Take-Home Calculator',
    url: canonicalUrl,
    description: 'Calculate UK take-home pay from gross salary with tax and National Insurance deductions'
  });
  
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: 'Home', url: 'https://salarytools.co.uk' },
    { name: 'Tools', url: 'https://salarytools.co.uk/tools' },
    { name: 'Simple Take-Home Calculator', url: canonicalUrl }
  ]);

  return (
    <>
      {makeMeta({
        title: pageTitle,
        description: pageDescription,
        canonical: canonicalUrl,
        jsonLd: [webAppJsonLd, breadcrumbJsonLd]
      })}

      <Container maxWidth="lg">
        <Box sx={{ py: 4 }}>
          {/* Page Header */}
          <Typography 
            variant="h1" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem' },
              fontWeight: 600,
              mb: 2,
              color: 'text.primary'
            }}
          >
            Simple Take-Home Calculator
          </Typography>
          
          <Typography 
            variant="body1" 
            sx={{ 
              fontSize: '1.1rem',
              color: 'text.secondary',
              mb: 4,
              maxWidth: '600px'
            }}
          >
            Quick calculation of your monthly and annual take-home pay. 
            Enter your salary and see results instantly.
          </Typography>

          {/* Input Form */}
          <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 3 }}>
              {/* Input Mode Selector */}
              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend" sx={{ mb: 2, fontWeight: 600 }}>
                  Salary Input Type
                </FormLabel>
                <RadioGroup
                  row
                  value={inputMode}
                  onChange={handleInputModeChange}
                  aria-label="salary input type"
                >
                  <FormControlLabel 
                    value="annual" 
                    control={<Radio />} 
                    label="Annual" 
                  />
                  <FormControlLabel 
                    value="monthly" 
                    control={<Radio />} 
                    label="Monthly" 
                  />
                </RadioGroup>
              </FormControl>

              {/* Salary Input */}
              <TextField
                fullWidth
                label={`${inputMode === 'annual' ? 'Annual' : 'Monthly'} Salary`}
                value={salaryInput}
                onChange={handleSalaryChange}
                error={!!error}
                helperText={error}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                }}
                placeholder={inputMode === 'annual' ? '50000' : '4167'}
                sx={{ mb: 3 }}
              />

              {/* Calculate Button */}
              <Button
                variant="contained"
                size="large"
                onClick={handleCalculate}
                disabled={!salaryInput || !!error}
                sx={{ 
                  py: 1.5,
                  px: 4,
                  fontSize: '1.1rem'
                }}
              >
                Calculate Take-Home Pay
              </Button>
            </CardContent>
          </Card>

          {/* Results */}
          {results && (
            <Card>
              <CardContent sx={{ p: 3 }}>
                <Typography 
                  variant="h2" 
                  sx={{ 
                    fontSize: '1.5rem',
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary'
                  }}
                >
                  Your Take-Home Pay
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* Monthly Take-Home */}
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textTransform: 'uppercase',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        letterSpacing: '1px',
                        color: 'text.secondary',
                        mb: 0.5
                      }}
                    >
                      Take-Home (Monthly)
                    </Typography>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontSize: '1.8rem',
                        fontWeight: 400,
                        color: 'text.primary'
                      }}
                    >
                      £{formatCurrencyGBP(results.monthly)}
                    </Typography>
                  </Box>

                  {/* Annual Take-Home */}
                  <Box>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textTransform: 'uppercase',
                        fontSize: '0.8rem',
                        fontWeight: 600,
                        letterSpacing: '1px',
                        color: 'text.secondary',
                        mb: 0.5
                      }}
                    >
                      Take-Home (Annual)
                    </Typography>
                    <Typography 
                      variant="h3" 
                      sx={{ 
                        fontSize: '1.8rem',
                        fontWeight: 400,
                        color: 'text.primary'
                      }}
                    >
                      £{formatCurrencyGBP(results.annual)}
                    </Typography>
                  </Box>
                </Box>

                {/* Additional Info */}
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Based on {config?.taxYear || '2025'} tax rates for {config?.nation === 'scotland' ? 'Scotland' : 'England & Wales'}. 
                    Calculation includes Income Tax and National Insurance only.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          )}
          
          {/* SEO Content Section with HMRC Links */}
          <Box sx={{ mt: 8, maxWidth: '800px', mx: 'auto' }}>
            <Typography variant="h2" component="h2" sx={{ 
              fontSize: '1.75rem', 
              fontWeight: 'bold', 
              mb: 3,
              color: 'text.primary'
            }}>
              How UK Take-Home Pay Calculation Works
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              This simple take-home pay calculator uses the latest{' '}
              <Link 
                href="https://www.gov.uk/income-tax-rates/current-rates-and-allowances" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                HMRC tax rates and allowances
              </Link>
              {' '}to calculate your net pay after Income Tax and National Insurance deductions. The calculator handles both annual and monthly salary inputs, automatically converting between the two for accurate calculations.
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              For the 2025-26 tax year, the personal allowance is £12,570, meaning this amount is tax-free for most UK taxpayers. Income above this threshold is subject to{' '}
              <Link 
                href="https://www.gov.uk/income-tax-rates" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                UK Income Tax rates
              </Link>
              : 20% basic rate (£12,571 to £50,270), 40% higher rate (£50,271 to £125,140), and 45% additional rate (above £125,140).
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 3, lineHeight: 1.7 }}>
              National Insurance contributions are calculated separately using{' '}
              <Link 
                href="https://www.gov.uk/national-insurance-rates" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                HMRC National Insurance rates
              </Link>
              . You pay 12% on earnings between £12,570 and £50,270, then 2% on earnings above £50,270. These contributions go towards your State Pension and other benefits.
            </Typography>
            
            <Typography variant="body1" sx={{ mb: 4, lineHeight: 1.7 }}>
              This calculator provides a quick estimate for basic tax and National Insurance calculations. For more complex scenarios including pension contributions, student loans, or other deductions, consider using our{' '}
              <Link 
                href="/salary-calculator"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                full salary calculator
              </Link>
              . All calculations are based on official rates published by{' '}
              <Link 
                href="https://www.gov.uk/government/organisations/hm-revenue-customs" 
                target="_blank" 
                rel="noopener noreferrer"
                sx={{ color: 'primary.main', textDecoration: 'underline' }}
              >
                HM Revenue and Customs
              </Link>
              .
            </Typography>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default TakeHomeSimple;